import { isConsentRolloutEnabled } from '../../core/consentRollout';
import storageKeys from '../../core/storageKeys';

/**
 * Shopify Customer Privacy API consent 门控（严格隐私模式，需求对齐 2026-09）。
 * 双重条件：灰度判定 && consent 判定（灰度名单在 core/consentRollout.js，与本能力
 * 解耦——扩灰度/全量放开只动那一处；名单命中即武装，店铺侧零操作）。
 * script URL 的 cm=1 曾是第三重条件（后端 strictPrivacy metafield→liquid 透出），2026-09
 * 简化为 SDK 忽略、透出链路闲置（全量接入不必逐店开 metafield）。未武装（灰度名单外）
 * 零行为直通。
 * 判定口径（PRD）：Analytics 与 Marketing 均授权且未启用 GPC 才追踪；
 * 状态不可用保持关闭（fail-closed——需求明文，取代一期 3s fail-open）。
 * 武装后无论未授权延迟启动还是已授权立即启动，均 start(true) 丢同意前积压（不补采不回填）。
 */

var API_TIMEOUT_MS = 3000;
var apiReady = false;
var apiFailed = false; // 仅 ensureApi（逻辑不变）仍写入；fail-closed 后读取侧不再消费此标记
var gatingArmed = false; // runWhenTrackingAllowed 判定灰度命中后置 true
var started = false;
var changeListeners = [];

/**
 * marketing 授权读取：Shopify 现行 API 方法名为 marketingAllowed()——2026-09-11 在测试店
 * 实抓 storefront-banner.js 与 consent-tracking-api.js v0.1 核实，marketingProcessingAllowed
 * 0 处出现（官方文档同步，shopify.dev 全站搜索旧名 0 结果）；历史第三方资料曾用旧名，双探测兜底。
 * 两个名字皆缺失仍视为未授权（fail-closed 判定不变）。
 */
function marketingConsentGranted(privacy) {
  if (typeof privacy.marketingAllowed === 'function') {
    return privacy.marketingAllowed() !== false;
  }
  if (typeof privacy.marketingProcessingAllowed === 'function') {
    return privacy.marketingProcessingAllowed() !== false;
  }
  return false;
}

/** PRD 判定：Analytics && Marketing 双授权 && 未启用 GPC（marketing API 缺失视为未授权） */
function consentGranted(privacy) {
  if (privacy.analyticsProcessingAllowed() === false) return false;
  if (!marketingConsentGranted(privacy)) return false;
  try {
    if (navigator.globalPrivacyControl === true) return false;
  } catch (e) {}
  return true;
}

function readAllowed() {
  // 未武装（非白名单/未开严格模式）：与旧版一致恒 true（track 守卫直通）
  if (!gatingArmed) return true;
  // fail-closed：API 未就绪/读取异常/接口缺失时保持关闭（需求：状态不可用时保持关闭）
  if (!apiReady) return false;
  try {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
    return consentGranted(privacy);
  } catch (e) {
    return false;
  }
}

function isTrackingAllowed() {
  return readAllowed();
}

function notifyChange() {
  var allowed = readAllowed();
  for (var i = 0; i < changeListeners.length; i++) {
    try {
      changeListeners[i](allowed);
    } catch (e) {}
  }
}

function ensureApi(done) {
  var settled = false;
  var finish = function () {
    if (settled) return;
    settled = true;
    done();
  };
  try {
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') {
      apiFailed = true;
      finish();
      return;
    }
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
      if (err) {
        apiFailed = true; // fail-open
      } else {
        apiReady = true;
        document.addEventListener('visitorConsentCollected', notifyChange);
      }
      finish();
    });
    setTimeout(finish, API_TIMEOUT_MS); // 超时 fail-open
  } catch (e) {
    apiFailed = true;
    finish();
  }
}

/**
 * 门控启动器。白名单外 → 立即 start(false)（零行为变化，队列全量 flush 与旧版一致）；
 * 白名单内 → 等 consent API：未授权挂起、Accept 后 start(true)；已授权也走 start(true)。
 * 已授权路径同样丢积压的原因（终审 I-1）：visitorConsentCollected 监听只在 loadFeatures
 * 成功回调内注册，Accept 可能先于该回调落定（慢网下常见，非极窄窗），事件已丢失——此后
 * API 落定 readAllowed()=true 走立即路径，队列里可能混有同意前入队的行为指令（第三方/
 * 商家主题代码的 pre-Accept track），若 start(false) 全量 flush 即补采，违反「不补采不回填」
 * ——宁丢不补（fail-closed 于补采）。对「cookie 已授权先于页面加载」场景无损：该场景
 * pre-start 队列本就只有 init/set 配置指令，过滤后等价全量重放。
 */
function runWhenTrackingAllowed(companyId, start) {
  if (!isConsentRolloutEnabled(companyId)) {
    start(false);
    return;
  }
  gatingArmed = true;
  ensureApi(function () {
    if (readAllowed()) {
      if (!started) {
        started = true;
        // I-1：不走 start(false) 全量 flush——Accept 可能先于 loadFeatures 回调落定（事件已
        // 丢失），队列可能混有同意前指令；init/set 照常重放（授权先于页面加载时本就只有配置指令）
        start(true);
      }
      return;
    }
    changeListeners.push(function (allowed) {
      if (allowed && !started) {
        started = true;
        start(true);
      }
      if (!allowed) {
        purgeIdentifiers();
      }
    });
  });
}

function onConsentChange(cb) {
  if (typeof cb === 'function') {
    changeListeners.push(cb);
  }
}

/** 撤回授权：清除 CartSee 自建标识（不动 Shopify 自有 cookie/storage） */
function purgeIdentifiers() {
  try {
    var s = window.localStorage;
    if (!s) return;
    s.removeItem(storageKeys.CARTSEE_UID);
    s.removeItem(storageKeys.CARTSEE_UID_LEGACY);
    s.removeItem(storageKeys.CARTX_CID);
    // 旧 key：部分平台变体仍写 'cid'；URL ?cid= 是后端可解密的 contactId，撤回必须清（终审 C2）
    s.removeItem(storageKeys.CID);
    // react-plugin 以 companyUUID 为键存的 uid 副本：键值形如 "<uuid8>-<v4>-<ts>"
    for (var i = s.length - 1; i >= 0; i--) {
      var k = s.key(i);
      var v = s.getItem(k);
      if (k && v && /^\S{6,32}-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-\d+$/.test(v)) {
        s.removeItem(k);
      }
    }
  } catch (e) {}
}

export default {
  isTrackingAllowed: isTrackingAllowed,
  runWhenTrackingAllowed: runWhenTrackingAllowed,
  onConsentChange: onConsentChange,
  purgeIdentifiers: purgeIdentifiers
};
